import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Storefront, Check, ArrowRight, CreditCard,
  CurrencyBtc, ChartLine, Users, Gift, Lightning
} from '@phosphor-icons/react';
import GlobalNav from '../../components/GlobalNav';
import { useLanguage } from '../../contexts/LanguageContext';

const MerchantPricing = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const plans = [
    {
      name: t('pricing_free'),
      price: '£0',
      period: t('pricing_free_period'),
      description: t('pricing_free_desc'),
      features: [
        t('pricing_free_f1'),
        t('pricing_free_f2'),
        t('pricing_free_f3'),
        t('pricing_free_f4'),
        t('pricing_free_f5')
      ],
      cta: t('pricing_cta_free'),
      highlight: false
    },
    {
      name: t('pricing_basic'),
      price: '£29',
      period: t('pricing_basic_period'),
      description: t('pricing_basic_desc'),
      features: [
        t('pricing_basic_f1'),
        t('pricing_basic_f2'),
        t('pricing_basic_f3'),
        t('pricing_basic_f4'),
        t('pricing_basic_f5'),
        t('pricing_basic_f6'),
        t('pricing_basic_f7')
      ],
      cta: t('pricing_cta_basic'),
      highlight: false
    },
    {
      name: t('pricing_advanced'),
      price: '£79',
      period: t('pricing_advanced_period'),
      description: t('pricing_advanced_desc'),
      features: [
        t('pricing_advanced_f1'),
        t('pricing_advanced_f2'),
        t('pricing_advanced_f3'),
        t('pricing_advanced_f4'),
        t('pricing_advanced_f5'),
        t('pricing_advanced_f6'),
        t('pricing_advanced_f7'),
        t('pricing_advanced_f8')
      ],
      cta: t('pricing_cta_advanced'),
      highlight: true,
      badge: t('pricing_popular')
    }
  ];

  const features = [
    {
      icon: Gift,
      title: t('pricing_compare_giftcards'),
      basic: '5% commission',
      advanced: '5% commission'
    },
    {
      icon: CreditCard,
      title: t('pricing_compare_chargecard'),
      basic: '—',
      advanced: '1.5-2.0%'
    },
    {
      icon: CurrencyBtc,
      title: t('pricing_compare_crypto'),
      basic: '—',
      advanced: t('pricing_compare_included')
    },
    {
      icon: ChartLine,
      title: t('pricing_compare_analytics'),
      basic: t('pricing_compare_basic_analytics'),
      advanced: t('pricing_compare_advanced_analytics')
    },
    {
      icon: Users,
      title: t('pricing_compare_support'),
      basic: t('pricing_compare_email'),
      advanced: t('pricing_compare_dedicated')
    }
  ];

  return (
    <div className="min-h-screen bg-[#0F1A14] text-white">
      <GlobalNav />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-[#1E2F26] border border-[#283D32] px-4 py-2 rounded-full mb-8"
          >
            <Storefront size={16} className="text-[#C3A365]" />
            <span className="text-sm text-[#8BA89A]">{t('pricing_badge')}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl sm:text-6xl font-light tracking-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('pricing_title')}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-[#8BA89A] max-w-xl mx-auto"
          >
            {t('pricing_subtitle')}
          </motion.p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="px-6 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-6">
            {plans.map((plan, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`relative p-8 border ${
                  plan.highlight
                    ? 'bg-gradient-to-b from-[#C3A365]/15 to-transparent border-[#C3A365]/50'
                    : 'bg-[#1E2F26]/30 border-[#283D32]'
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-[#C3A365] text-[#0F1A14] text-xs font-bold uppercase tracking-wider">
                    {plan.badge}
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-medium text-white mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-light text-[#C3A365]">{plan.price}</span>
                    <span className="text-[#8BA89A]">{plan.period}</span>
                  </div>
                  <p className="text-[#8BA89A] text-sm mt-2">{plan.description}</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <Check size={18} className="text-[#C3A365] flex-shrink-0 mt-0.5" weight="bold" />
                      <span className="text-sm text-white">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => navigate('/activer')}
                  className={`w-full py-4 font-semibold transition-all ${
                    plan.highlight
                      ? 'bg-[#C3A365] text-[#0F1A14] hover:bg-[#D4B576]'
                      : 'border border-[#3E5248] text-white hover:border-[#C3A365]/50'
                  }`}
                  data-testid={`pricing-cta-${i}`}
                >
                  {plan.cta}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="px-6 py-16 bg-[#18261E]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-light text-center mb-12" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('pricing_compare_title')}
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#283D32]">
                  <th className="text-left py-4 text-[#8BA89A] font-normal">{t('pricing_compare_feature')}</th>
                  <th className="text-center py-4 text-white font-medium">{t('pricing_basic')}</th>
                  <th className="text-center py-4 text-[#C3A365] font-medium">{t('pricing_advanced')}</th>
                </tr>
              </thead>
              <tbody>
                {features.map((feature, i) => {
                  const Icon = feature.icon;
                  return (
                    <tr key={i} className="border-b border-[#283D32]/50">
                      <td className="py-4">
                        <div className="flex items-center gap-3">
                          <Icon size={18} className="text-[#8BA89A]" />
                          <span className="text-white">{feature.title}</span>
                        </div>
                      </td>
                      <td className="text-center py-4 text-[#8BA89A]">{feature.basic}</td>
                      <td className="text-center py-4 text-[#C3A365]">{feature.advanced}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Commission Details */}
      <section className="px-6 py-16 bg-[#0F1A14]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-light text-center mb-12" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('pricing_commission_title')}
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-[#1E2F26] border border-[#283D32] text-center">
              <Gift size={32} className="text-[#C3A365] mx-auto mb-4" />
              <h3 className="text-white font-medium mb-2">{t('pricing_compare_giftcards')}</h3>
              <p className="text-3xl text-[#C3A365] font-light mb-2">5%</p>
              <p className="text-[#8BA89A] text-sm">{t('pricing_commission_giftcard')}</p>
              <p className="text-[#8BA89A] text-xs mt-2">{t('pricing_commission_giftcard_note')}</p>
            </div>

            <div className="p-6 bg-[#1E2F26] border border-[#283D32] text-center">
              <CreditCard size={32} className="text-[#C3A365] mx-auto mb-4" />
              <h3 className="text-white font-medium mb-2">{t('pricing_compare_chargecard')}</h3>
              <p className="text-3xl text-[#C3A365] font-light mb-2">1.5-2%</p>
              <p className="text-[#8BA89A] text-sm">{t('pricing_commission_chargecard')}</p>
              <p className="text-[#8BA89A] text-xs mt-2">{t('pricing_commission_chargecard_note')}</p>
            </div>

            <div className="p-6 bg-[#1E2F26] border border-[#283D32] text-center">
              <CurrencyBtc size={32} className="text-[#C3A365] mx-auto mb-4" />
              <h3 className="text-white font-medium mb-2">{t('pricing_compare_crypto')}</h3>
              <p className="text-3xl text-[#C3A365] font-light mb-2">1%</p>
              <p className="text-[#8BA89A] text-sm">{t('pricing_commission_crypto')}</p>
              <p className="text-[#8BA89A] text-xs mt-2">{t('pricing_commission_crypto_note')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-24 bg-gradient-to-b from-[#18261E] to-[#0F1A14]">
        <div className="max-w-3xl mx-auto text-center">
          <Lightning size={48} className="text-[#C3A365] mx-auto mb-6" weight="duotone" />
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('pricing_cta_final_title')}
          </h2>
          <p className="text-[#8BA89A] mb-8">
            {t('pricing_cta_final_desc')}
          </p>
          <motion.button
            onClick={() => navigate('/contact')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#C3A365] text-[#0F1A14] font-semibold hover:bg-[#D4B576] transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="pricing-final-cta"
          >
            {t('pricing_cta_final_btn')} <ArrowRight size={18} />
          </motion.button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#1E2F26]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="brand-logo text-xl text-[#8BA89A]">habitué</span>
          <p className="text-xs text-[#8BA89A]">© 2025 habitué. {t('footer_rights')}</p>
        </div>
      </footer>
    </div>
  );
};

export default MerchantPricing;
